
import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import { Smartphone, Globe, Tag, Filter } from 'lucide-react';

interface AnalyticsChartsProps {
    periodDays: number;
}

interface BreakdownItem {
    key: string;
    clicks: number;
    conversions: number;
    revenue: number;
    commissions: number;
}

export default function AnalyticsCharts({ periodDays }: AnalyticsChartsProps) {
    const [breakdownType, setBreakdownType] = useState<string>('device');
    const [data, setData] = useState<BreakdownItem[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchBreakdown();
    }, [periodDays, breakdownType]);

    const fetchBreakdown = async () => {
        setLoading(true);
        try {
            const getStats = httpsCallable(functions, 'getStats');
            const result = await getStats({ period_days: periodDays, breakdown: breakdownType });
            const response = result.data as any;

            if (response.success && response.breakdown) {
                const formattedData = Object.entries(response.breakdown).map(([key, stats]: [string, any]) => ({
                    key,
                    ...stats
                }));

                // Sort by revenue desc
                formattedData.sort((a, b) => b.revenue - a.revenue);
                setData(formattedData);
            }
        } catch (error) {
            console.error('Error fetching breakdown:', error);
        } finally {
            setLoading(false);
        }
    };

    const COLORS = ['#667eea', '#764ba2', '#6366f1', '#8b5cf6', '#d946ef', '#f43f5e'];

    return (
        <div className="card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Filter size={20} className="text-primary-400" />
                    Detailed Breakdown
                </h2>

                <div className="flex bg-white/5 p-1 rounded-lg">
                    <button
                        onClick={() => setBreakdownType('device')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${breakdownType === 'device' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        <Smartphone size={16} />
                        Device
                    </button>
                    <button
                        onClick={() => setBreakdownType('country')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${breakdownType === 'country' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        <Globe size={16} />
                        Country
                    </button>
                    <button
                        onClick={() => setBreakdownType('sub_id')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${breakdownType === 'sub_id' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        <Tag size={16} />
                        Sub ID
                    </button>
                    <button
                        onClick={() => setBreakdownType('source_id')}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${breakdownType === 'source_id' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
                            }`}
                    >
                        <Tag size={16} />
                        Source
                    </button>
                </div>
            </div>

            <div className="h-80 w-full">
                {loading ? (
                    <div className="h-full flex items-center justify-center">
                        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : data.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-gray-500">
                        No data available for this period
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" horizontal={false} />
                            <XAxis type="number" stroke="#9ca3af" fontSize={12} />
                            <YAxis
                                type="category"
                                dataKey="key"
                                stroke="#9ca3af"
                                fontSize={12}
                                width={100}
                                tickFormatter={(value) => value.length > 10 ? `${value.substring(0, 10)}...` : value}
                            />
                            <Tooltip
                                cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                                contentStyle={{
                                    backgroundColor: 'rgba(15, 15, 35, 0.95)',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: '12px',
                                    color: '#fff',
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                                }}
                                formatter={(value: any, name: any) => {
                                    if (name === 'revenue' || name === 'commissions') return [`€${Number(value).toFixed(2)}`, name === 'revenue' ? 'Revenue' : 'Commissions'];
                                    return [value, name === 'clicks' ? 'Clicks' : 'Conversions'];
                                }}
                            />
                            <Bar dataKey="revenue" name="revenue" radius={[0, 4, 4, 0]} barSize={20}>
                                {data.map((_, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>

            {/* Data Table */}
            <div className="mt-6 overflow-x-auto max-h-60 overflow-y-auto custom-scrollbar">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-400 uppercase bg-white/5 sticky top-0">
                        <tr>
                            <th className="px-4 py-3 rounded-tl-lg">{breakdownType === 'device' ? 'Device Type' : breakdownType === 'country' ? 'Country' : 'ID'}</th>
                            <th className="px-4 py-3 text-right">Clicks</th>
                            <th className="px-4 py-3 text-right">Conversions</th>
                            <th className="px-4 py-3 text-right">Conv. Rate</th>
                            <th className="px-4 py-3 text-right rounded-tr-lg">Revenue</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item, index) => (
                            <tr key={index} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                <td className="px-4 py-3 font-medium text-white">{item.key === 'Unknown' ? 'Unknown / Not Set' : item.key}</td>
                                <td className="px-4 py-3 text-right text-gray-300">{item.clicks.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-gray-300">{item.conversions.toLocaleString()}</td>
                                <td className="px-4 py-3 text-right text-gray-300">
                                    {item.clicks > 0 ? ((item.conversions / item.clicks) * 100).toFixed(2) : '0.00'}%
                                </td>
                                <td className="px-4 py-3 text-right text-emerald-400 font-medium">€{item.revenue.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
